import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import 'leaflet/dist/leaflet.css';
import React from 'react';
import { Provider } from 'react-redux';
import { store } from './store/index.ts';
import { initI18n } from './locals/i18n.ts';
import * as Sentry from '@sentry/react';
import { CONFIG } from './lib/config';
import VConsole from 'vconsole';

async function root() {
  await initI18n();

  if (CONFIG.sentry_dsn) {
    Sentry.init({
      dsn: CONFIG.sentry_dsn,
      environment: import.meta.env.MODE || 'production',
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      debug: import.meta.env.DEV,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });

    // Test Sentry with a test message
    Sentry.addBreadcrumb({
      message: 'Sentry initialized successfully',
      level: 'info',
    });

    // Add global error handler for unhandled promises
    window.addEventListener('unhandledrejection', event => {
      Sentry.captureException(event.reason);
    });
  } else {
    console.warn('Sentry DSN not found. Error tracking disabled.');
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>
  );

  const isDebugHost = ['global-move-two.vercel.app', 'global-move-prerelease.vercel.app'].includes(
    window.location.hostname
  );

  if (import.meta.env.DEV || isDebugHost) {
    const vConsole = new VConsole({
      theme: 'dark',
      maxLogNumber: 500,
    });
    const defaultSwitch = document.querySelector<HTMLElement>('#__vconsole .vc-switch');
    defaultSwitch?.style.setProperty('display', 'none', 'important');

    // Пока открыта шторка (Radix Dialog), у body стоит pointer-events: none, и
    // всё, что лежит прямо в body, — кнопка и панель vConsole — перестаёт
    // ловить тапы. Возвращаем им события явно: консоль нужна как раз тогда,
    // когда что-то не так на открытом экране.
    const debugStyle = document.createElement('style');
    debugStyle.textContent = '#__vconsole { pointer-events: auto !important; }';
    document.head.appendChild(debugStyle);

    const debugButton = document.createElement('button');
    debugButton.type = 'button';
    debugButton.setAttribute('aria-label', 'Open debug console');
    debugButton.textContent = '›';
    Object.assign(debugButton.style, {
      position: 'fixed',
      right: '8px',
      // Верхняя полоса под шапкой Telegram: шторки занимают не больше 85dvh
      // снизу, так что здесь кнопку ничем не перекрывает. Перетащить можно.
      top: 'calc(var(--tg-content-safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px) + 12px)',
      width: '32px',
      height: '48px',
      zIndex: '2147483647',
      pointerEvents: 'auto',
      border: '0',
      borderRadius: '16px 0 0 16px',
      background: 'rgba(30, 30, 30, 0.82)',
      color: '#fff',
      fontSize: '30px',
      lineHeight: '1',
      cursor: 'grab',
      touchAction: 'none',
      userSelect: 'none',
    });

    let dragged = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    debugButton.addEventListener('pointerdown', event => {
      dragged = false;
      startX = event.clientX;
      startY = event.clientY;
      const rect = debugButton.getBoundingClientRect();
      startLeft = rect.left;
      startTop = rect.top;
      debugButton.setPointerCapture(event.pointerId);
      debugButton.style.cursor = 'grabbing';
    });

    debugButton.addEventListener('pointermove', event => {
      if (!debugButton.hasPointerCapture(event.pointerId)) return;
      const nextLeft = Math.max(
        0,
        Math.min(window.innerWidth - debugButton.offsetWidth, startLeft + event.clientX - startX),
      );
      const nextTop = Math.max(
        0,
        Math.min(window.innerHeight - debugButton.offsetHeight, startTop + event.clientY - startY),
      );
      if (Math.abs(event.clientX - startX) > 4 || Math.abs(event.clientY - startY) > 4) {
        dragged = true;
      }
      debugButton.style.left = `${nextLeft}px`;
      debugButton.style.top = `${nextTop}px`;
      debugButton.style.right = 'auto';
      debugButton.style.bottom = 'auto';
    });

    debugButton.addEventListener('pointerup', event => {
      if (debugButton.hasPointerCapture(event.pointerId)) {
        debugButton.releasePointerCapture(event.pointerId);
      }
      debugButton.style.cursor = 'grab';
      if (!dragged) vConsole.show();
    });

    document.body.appendChild(debugButton);
  }
}

root();
